/**
 * One file, for reviewing client data: header, figures, how it was classified,
 * then the rows themselves (see `FileDataWorkbench`).
 *
 * The CONFLICT badge stays on the page rather than in a drawer: it says the
 * declared type disagrees with the content.
 */

import { useFollowRecordModule, useModule } from '../modules/useModule';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import IngestProgress from './IngestProgress';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	ConfirmDialog,
	Detail,
	Dialog,
	ErrorNotice,
	Note,
	StatusBadge,
} from '../../shared/ui';
import { InfoHint } from '../../shared/InfoHint';
import { useToast } from '../../provider/toast';
import {
	useDeleteFileMutation,
	useExcludeFileMutation,
	useFileClassificationRulesQuery,
	useFileWithClassifiedAtQuery,
	useFirmClassificationRulesQuery,
	useReapplyClassificationMutation,
	useRequestReplacementMutation,
	useRetryFileMutation,
} from '../../queries/intake';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../provider/auth';
import { count } from '../../utils/format';
import { useCanonicalFieldsQuery } from '../../queries/firm';
import { needsProcessAgain, needsProcessing } from '../../utils/files';
import { ProcessAgain } from './ProcessAgain';
import { ErrorState, LoadingState } from '../shared/states';
import { conflicts, declaredLabel, detectedLabel } from '../shared/badges';
import { downloadFile } from '../files/FileTable';
import type { ReapplyResult } from '../../schemas';
import { PageHeader } from '../../provider/pageTitle';
import { useClientCrumbs } from './crumbs';
import { Button, Menu, MenuItem } from '@mui/material';
import { keys } from '../../queries/keys';
import { fileRowsBase } from '../../api/FileRows';
import { FileDataWorkbench, fileMetaLine } from '../files/FileDataWorkbench';
import { FileClassificationSection } from '../files/FileClassificationSection';
import { FileColumnsDialog, type FileColumnsView } from './FileColumnsDialog';

export default function FileDetail() {
	const { clientId = '', fileId = '' } = useParams();
	const { permissions } = useAuth();
	/* `/review` and re-apply sit behind the `mapFormats` gate; checked here too
     so a link never depends on this page's route guard staying narrow. */
	const canProcess = Boolean(permissions?.mapFormats);
	const [excluding, setExcluding] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const deleteMutation = useDeleteFileMutation();
	const navigate = useNavigate();
	const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
	const queryClient = useQueryClient();
	const [replacing, setReplacing] = useState(false);
	/* Which columns window is open, if any (data preparation only). */
	const [columnsView, setColumnsView] = useState<FileColumnsView | null>(
		null,
	);
	const file = useFileWithClassifiedAtQuery(fileId ?? null);
	/* An H2R file has no classification: no rules, no review, no re-apply. */
	const isH2R = file.data?.module === 'H2R';
	const fields = useCanonicalFieldsQuery(file.data?.module);
	const labelOf = (key: string) =>
		fields.data?.fields.find((field) => field.field === key)?.label ?? key;
	const [reapplying, setReapplying] = useState(false);
	const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(
		new Set(),
	);
	const [reapplyResult, setReapplyResult] = useState<ReapplyResult | null>(
		null,
	);

	/* The file's module wins over the URL's, for the badge and every link. */
	useFollowRecordModule(file.data?.module);
	/* The file's module names the module crumb. */
	const clientCrumbs = useClientCrumbs(clientId, file.data?.module);
	const { module, link } = useModule();
	/* `status=ACTIVE` is load-bearing: without it the endpoint answers with the
     DRAFT ruleset, and the picker would offer rules re-apply then refuses.
     The rules are the module's own (the URL follows the file's, above). */
	const ruleOptions = useFirmClassificationRulesQuery(
		'ACTIVE',
		module,
		!isH2R,
	);

	/* Which rules this file was actually classified with, and what the re-apply
     drawer pre-ticks. `ruleIds` is already every active rule when
     `hasSelection` is false. The server reads it behind `mapFormats`, so it is
     asked for only by someone who may. */
	const recordedRules = useFileClassificationRulesQuery(
		canProcess && !isH2R ? fileId || null : null,
	);

	const toast = useToast();

	const reapplyMutation = useReapplyClassificationMutation(fileId ?? '');
	const reapply = {
		isPending: reapplyMutation.isPending,
		error: reapplyMutation.error,
		reset: reapplyMutation.reset,
		run: (ruleIds: string[]) =>
			reapplyMutation.mutate(ruleIds, {
				onSuccess: (result: ReapplyResult) => {
					setReapplyResult(result);
					/* The rows and the figures above them have just changed. */
					void queryClient.invalidateQueries({
						queryKey: keys.fileRows.all(fileRowsBase(fileId)),
					});
					toast('ok', 'Classification re-applied.');
				},
			}),
	};

	const retryMutation = useRetryFileMutation(fileId ?? '');
	const retry = {
		isPending: retryMutation.isPending,
		error: retryMutation.error,
		run: () =>
			retryMutation.mutate(undefined, {
				onSuccess: () => toast('ok', 'File re-read.'),
			}),
	};

	const replacementMutation = useRequestReplacementMutation(fileId ?? '');
	const requestReplacement = {
		isPending: replacementMutation.isPending,
		error: replacementMutation.error,
		run: (reason: string) =>
			replacementMutation.mutate(
				{ note: reason },
				{
					onSuccess: () => {
						setReplacing(false);
						toast(
							'ok',
							'Replacement requested. It now shows as outstanding in this library.',
						);
					},
				},
			),
	};

	const excludeMutation = useExcludeFileMutation(fileId ?? '');
	const exclude = {
		isPending: excludeMutation.isPending,
		error: excludeMutation.error,
		run: (reason: string) =>
			excludeMutation.mutate(
				{ reason },
				{
					onSuccess: () => {
						setExcluding(false);
						toast(
							'ok',
							'File excluded, with the reason on the record.',
						);
					},
				},
			),
	};

	const download = useMutation({ mutationFn: downloadFile });

	if (file.error) {
		return (
			<ErrorState
				error={file.error}
				onRetry={file.refetch}
				hint={
					<>
						Every file is reachable from{' '}
						<Link
							className="link"
							to={link(`/intake/clients/${clientId}`)}
						>
							the client&apos;s files
						</Link>
						.
					</>
				}
			/>
		);
	}
	if (file.isPending) return <LoadingState label="Loading the file…" />;
	if (!file.data) return null;

	const current = file.data;
	const library = `/intake/clients/${clientId}`;
	const needsMapping = current.status === 'MAPPING_REQUIRED';
	const clash = conflicts(current);
	const canRequest =
		canProcess &&
		current.status !== 'REPLACED' &&
		current.status !== 'AWAITING_REPLACEMENT';
	const canExclude =
		canProcess &&
		current.status !== 'EXCLUDED' &&
		current.status !== 'REPLACED';
	/* Admins only (`deleteData`), the same gate as the API. */
	const canDelete = Boolean(permissions?.deleteData);

	/* Same scoping test `classificationService.scopedOrderedRules` applies, so
     the picker never offers a rule that could not match a row here. */
	const applicableRules = (ruleOptions.data?.rules ?? []).filter(
		(rule) => !rule.fkClientId || rule.fkClientId === current.fkClientId,
	);
	const diffKeys = reapplyResult
		? Array.from(
				new Set([
					...Object.keys(reapplyResult.before),
					...Object.keys(reapplyResult.after),
				]),
			).sort()
		: [];

	const openReapply = () => {
		/* `ruleIds`, and no fallback to `applicableRules`: ticking every active
       rule would silently widen a deliberate 2-of-4 selection. The button is
       disabled until the record read succeeds, so there is nothing to guess. */
		setSelectedRuleIds(new Set(recordedRules.data?.ruleIds ?? []));
		setReapplyResult(null);
		reapply.reset();
		setReapplying(true);
	};
	const toggleRule = (ruleId: string, checked: boolean) =>
		setSelectedRuleIds((currentIds) => {
			const next = new Set(currentIds);
			if (checked) next.add(ruleId);
			else next.delete(ruleId);
			return next;
		});

	return (
		<div className="stack ops-page dense-page">
			<PageHeader
				breadcrumbs={clientCrumbs}
				title={current.originalName}
				subtitle={fileMetaLine(current) || undefined}
				hint={
					<>
						<StatusBadge
							group="fileStatus"
							value={current.status}
						/>
						{clash && (
							<span className="bdg warning plain nowrap">
								declared{' '}
								{declaredLabel(
									current.declaredAs,
								).toLowerCase()}
								<InfoHint label="Why this disagrees">
									<p className="ih-title">
										Declared type conflicts with content
									</p>
									<p>
										Declared as{' '}
										{declaredLabel(current.declaredAs)}; the
										rows read as {detectedLabel(current)}.
										The content decides what each row is — a
										silent correction is how a returns-only
										export becomes a sales figure.
									</p>
								</InfoHint>
							</span>
						)}
					</>
				}
				actions={
					<div
						className="row"
						style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}
					>
						<Button
							loading={download.isPending}
							variant="outlined"
							size="small"

							disabled={download.isPending}
							onClick={() => download.mutate(current.pkFileId)}
						>
							Download original file
						</Button>
						{permissions?.accessIntake && (
							<>
								<Button
									variant="outlined"
									size="small"
									onClick={() => setColumnsView('source')}
								>
									Source columns
								</Button>
								<Button
									variant="outlined"
									size="small"
									onClick={() => setColumnsView('mapping')}
								>
									Column mapping
								</Button>
							</>
						)}
						{current.status === 'FAILED' && (
							<Button
								loading={retry.isPending}
								variant="outlined"
								size="small"

								disabled={retry.isPending}
								onClick={() => retry.run()}
							>
								Retry read
							</Button>
						)}
						{(canRequest || canExclude || canDelete) && (
							<>
								<Button
									variant="outlined"
									size="small"
									aria-haspopup="menu"
									aria-expanded={Boolean(moreAnchor)}
									onClick={(event) =>
										setMoreAnchor(event.currentTarget)
									}
								>
									More
								</Button>
								<Menu
									anchorEl={moreAnchor}
									open={Boolean(moreAnchor)}
									onClose={() => setMoreAnchor(null)}
								>
									{canRequest && (
										<MenuItem
											onClick={() => {
												setMoreAnchor(null);
												setReplacing(true);
											}}
										>
											Request a new file…
										</MenuItem>
									)}
									{canExclude && (
										<MenuItem
											sx={{
												color: 'error.main',
												bgcolor: 'error.light',
												transition: 'all 0.3s',
												'&:hover': {
													bgcolor: 'error.dark',

													color: 'error.light',
												},
											}}
											onClick={() => {
												setMoreAnchor(null);
												setExcluding(true);
											}}
										>
											Exclude file
										</MenuItem>
									)}
									{canDelete && (
										<MenuItem
											sx={{ color: 'error.main' }}
											onClick={() => {
												setMoreAnchor(null);
												setDeleting(true);
											}}
										>
											Delete file…
										</MenuItem>
									)}
								</Menu>
							</>
						)}
						{needsMapping && (
							<Button
								component={Link}
								variant="contained"
								size="small"
								to={link(
									`${library}/files/${current.pkFileId}/mapping`,
								)}
							>
								Map columns
							</Button>
						)}
						{/* The only way back to the review screen: it is otherwise a
                one-shot redirect fired when a mapping is confirmed. */}
						{needsProcessing(current) && canProcess && (
							<Button
								component={Link}
								variant="contained"
								size="small"
								to={link(
									`${library}/files/${current.pkFileId}/review`,
								)}
							>
								Review rules
							</Button>
						)}
						{needsProcessAgain(current) && canProcess && (
							<ProcessAgain file={current} variant="button" />
						)}
					</div>
				}
			/>

			{/* Where someone who uploaded and navigated away finds out how far the
          ingest got. Renders nothing when there is no run. */}
			{(current.status === 'UPLOADED' || current.status === 'FAILED') && (
				<IngestProgress fileId={current.pkFileId} />
			)}
			{current.status === 'FAILED' && (
				<Note tone="danger" title="Could not be read" row>
					{current.statusNote}
				</Note>
			)}
			{current.status === 'AWAITING_REPLACEMENT' && (
				<Note tone="warn" title="Replacement requested" row>
					{current.statusNote}
				</Note>
			)}
			{Boolean(retry.error) && (
				<Note tone="danger" title="Retry did not help" row>
					{retry.error instanceof Error
						? retry.error.message
						: 'Something failed.'}
				</Note>
			)}
			{Boolean(download.error) && (
				<Note tone="danger" title="Could not download the original" row>
					{download.error instanceof Error
						? download.error.message
						: 'Something failed.'}
				</Note>
			)}

			<FileDataWorkbench
				file={current}
				between={() =>
					canProcess &&
					!isH2R && (
						<FileClassificationSection
							classifiedAt={current.classifiedAt}
							recorded={recordedRules.data}
							loading={recordedRules.isPending}
							error={recordedRules.error}
							onRetry={() => void recordedRules.refetch()}
							labelOf={labelOf}
							canReapply={canProcess}
							reapplyDisabled={
								ruleOptions.isPending ||
								recordedRules.isPending ||
								/* A failed read leaves the drawer with no idea what this file
                   was classified with, so it must not open at all. */
								Boolean(recordedRules.error) ||
								!recordedRules.data
							}
							reapplyTitle={
								recordedRules.error
									? 'The rules used for this file could not be loaded. Try again in a moment.'
									: undefined
							}
							onReapply={openReapply}
						/>
					)
				}
			/>

			<FileColumnsDialog
				fileId={current.pkFileId}
				fileName={current.originalName}
				view={columnsView}
				onClose={() => setColumnsView(null)}
			/>

			<ConfirmDialog
				open={replacing}
				onClose={() => setReplacing(false)}
				onConfirm={(reason) => requestReplacement.run(reason)}
				title={`Request a new file for ${current.originalName}?`}
				confirmLabel="Request a new file"
				reasonRequired
				pending={requestReplacement.isPending}
				error={requestReplacement.error}
				question={
					<>
						The file stays on record and shows as{' '}
						<b>Replacement requested</b> until a corrected file
						arrives. Say what is wrong with it — that is what the
						client needs.
					</>
				}
			/>

			<ConfirmDialog
				open={deleting}
				onClose={() => {
					setDeleting(false);
					deleteMutation.reset();
				}}
				onConfirm={() =>
					deleteMutation.mutate(current.pkFileId, {
						onSuccess: () => {
							setDeleting(false);
							toast('ok', `${current.originalName} deleted.`);
							navigate(link(library));
						},
					})
				}
				title={`Delete ${current.originalName}?`}
				confirmLabel="Delete file"
				danger
				pending={deleteMutation.isPending}
				error={deleteMutation.error}
				question={
					<>
						It will no longer appear in Data files, file groups or
						new audits. Audits that already used it keep their data.
					</>
				}
			/>

			<ConfirmDialog
				open={excluding}
				onClose={() => setExcluding(false)}
				onConfirm={(reason) => exclude.run(reason)}
				title={`Exclude ${current.originalName}?`}
				confirmLabel="Exclude file"
				reasonRequired
				danger
				pending={exclude.isPending}
				error={exclude.error}
				question={
					<>
						The file stays in the library but its rows leave every
						figure, and the reason is kept on the record. Any audit
						that already selected it shows it as excluded rather
						than losing it.
					</>
				}
			/>

			{!isH2R && (
				<Dialog
					open={reapplying}
					onClose={() => setReapplying(false)}
					title="Re-apply classification rules"
					subtitle={current.originalName}
					footer={
						reapplyResult ? (
							<div className="spread">
								<span className="small mut">
									{count(reapplyResult.rulesApplied)} rule
									application
									{reapplyResult.rulesApplied === 1
										? ''
										: 's'}{' '}
									claimed at least one row.
								</span>
								<Button
									variant="contained"

									onClick={() => setReapplying(false)}
								>
									Close
								</Button>
							</div>
						) : (
							<div className="spread">
								<Button
									variant="outlined"

									onClick={() => setReapplying(false)}
									disabled={reapply.isPending}
								>
									Cancel
								</Button>
								<Button
									loading={reapply.isPending}
									variant="contained"

									disabled={reapply.isPending}
									onClick={() =>
										reapply.run(Array.from(selectedRuleIds))
									}
								>
									Re-apply
								</Button>
							</div>
						)
					}
				>
					<div className="stack">
						<Note tone="info" row>
							Audits that already use this file&apos;s rows keep
							them as they were.
						</Note>

						<ErrorNotice error={reapply.error} />
						{Boolean(ruleOptions.error) && (
							<ErrorNotice
								error={ruleOptions.error}
								onRetry={ruleOptions.refetch}
							/>
						)}
						{Boolean(recordedRules.error) && (
							<ErrorNotice
								error={recordedRules.error}
								onRetry={recordedRules.refetch}
							/>
						)}

						{!reapplyResult &&
							(applicableRules.length === 0 ? (
								<p className="small mut">
									{ruleOptions.isPending
										? 'Loading the rules…'
										: 'No classification rules apply to this client yet.'}
								</p>
							) : (
								<div
									className="stack"
									style={{ gap: 'var(--s2)' }}
								>
									{selectedRuleIds.size === 0 && (
										<Note tone="warn" row>
											Nothing selected — every row on this
											file will come back unclassified.
										</Note>
									)}
									<div className="divide">
										{applicableRules.map((rule) => (
											<label
												key={
													rule.pkClassificationRuleId
												}
												className="row"
												style={{
													gap: 'var(--s2)',
													alignItems: 'flex-start',
													paddingTop: 'var(--s2)',
												}}
											>
												<input
													type="checkbox"
													checked={selectedRuleIds.has(
														rule.pkClassificationRuleId,
													)}
													onChange={(event) =>
														toggleRule(
															rule.pkClassificationRuleId,
															event.target
																.checked,
														)
													}
												/>
												<span className="small">
													When{' '}
													{labelOf(rule.matchField)}{' '}
													{rule.matchOperator
														.toLowerCase()
														.replace(
															/_/g,
															' ',
														)}{' '}
													&quot;
													{rule.matchValue}&quot;,
													classify as{' '}
													<StatusBadge
														group="classification"
														value={
															rule.classification
														}
														plain
													/>
													{!rule.fkClientId &&
														' — every client'}
													{rule.reason && (
														<>
															<br />
															<span className="mut">
																{rule.reason}
															</span>
														</>
													)}
												</span>
											</label>
										))}
									</div>
								</div>
							))}

						{reapplyResult && (
							<div className="stack">
								<p className="small mut">What changed:</p>
								<div
									className="stack"
									style={{ gap: 'var(--s1)' }}
								>
									{diffKeys.map((key) => (
										<Detail
											key={key}
											label={
												<StatusBadge
													group="classification"
													value={key}
													plain
												/>
											}
										>
											{count(
												reapplyResult.before[key] ?? 0,
											)}{' '}
											→{' '}
											{count(
												reapplyResult.after[key] ?? 0,
											)}
										</Detail>
									))}
								</div>
							</div>
						)}
					</div>
				</Dialog>
			)}
		</div>
	);
}
